import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  NativeSelect,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DnsIcon from '@mui/icons-material/Dns';
import BarChartIcon from '@mui/icons-material/BarChart';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import cronstrue from 'cronstrue';
import { CronExpressionParser } from 'cron-parser';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AutomationFormDialog from './AutomationFormDialog';
import AutomationsCalendar from './AutomationsCalendar';
import { cleanCronExpression } from './calendarUtils';

// Category metadata - matches Data Sources
const CATEGORY_ORDER = ['sales', 'labor', 'tips', 'cogs', 'other'];

const CATEGORY_LABELS = {
  sales: 'Sales',
  labor: 'Labor',
  tips: 'Tips',
  cogs: 'COGS',
  other: 'Other',
};

const CATEGORY_ICONS = {
  sales: PointOfSaleIcon,
  labor: GroupsIcon,
  tips: AttachMoneyIcon,
  cogs: RestaurantIcon,
  other: MoreHorizIcon,
};

const CATEGORY_COLORS = {
  sales: '#1976d2',
  labor: '#7b1fa2',
  tips: '#2e7d32',
  cogs: '#e65100',
  other: '#0288d1',
};

// Status colors
const STATUS_COLORS = {
  success: { color: '#2e7d32', label: 'Success' },
  failed: { color: '#c62828', label: 'Failed' },
  never: { color: '#9e9e9e', label: 'Never Run' },
};

// Platform filter options
const PLATFORM_FILTER_OPTIONS = [
  { value: '', label: 'All Platforms' },
  { value: 'snowflake', label: 'Snowflake' },
  { value: 'vm', label: 'Virtual Machine' },
  { value: 'powerbi', label: 'Power BI' },
];

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

/**
 * Parse a cron expression and return the next run time as a formatted string.
 */
const getNextRun = (cronExpr, timezone) => {
  if (!cronExpr) return '-';
  try {
    const cleanCron = cleanCronExpression(cronExpr);
    if (!cleanCron) return '-';

    const interval = CronExpressionParser.parse(cleanCron, {
      tz: timezone || 'America/Los_Angeles',
    });
    const next = interval.next().toDate();
    return next.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (err) {
    console.warn('Failed to parse cron expression:', cronExpr, err.message);
    return '-';
  }
};

/**
 * Convert cron expression to human-readable string.
 */
const getCronReadable = (cronExpr) => {
  try {
    return cronstrue.toString(cronExpr, { use24HourTimeFormat: false });
  } catch {
    return cronExpr;
  }
};

/**
 * Format a timestamp for display.
 */
const formatTimestamp = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Get platform icon and tooltip for an automation.
 */
const getPlatformIcon = (automation) => {
  const { platformType, platformHost, isSnowflakeTask } = automation;

  // Use explicit platformType if set
  if (platformType === 'snowflake') {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Snowflake</Typography>
            {platformHost && (
              <Typography variant="caption" display="block">{platformHost}</Typography>
            )}
          </Box>
        }
        arrow
      >
        <AcUnitIcon sx={{ fontSize: 16, color: '#29B5E8' }} />
      </Tooltip>
    );
  }

  if (platformType === 'vm') {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Virtual Machine</Typography>
            {platformHost && (
              <Typography variant="caption" display="block">Host: {platformHost}</Typography>
            )}
          </Box>
        }
        arrow
      >
        <DnsIcon sx={{ fontSize: 16, color: '#7B1FA2' }} />
      </Tooltip>
    );
  }

  if (platformType === 'powerbi') {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Power BI</Typography>
            {platformHost && (
              <Typography variant="caption" display="block">{platformHost}</Typography>
            )}
          </Box>
        }
        arrow
      >
        <BarChartIcon sx={{ fontSize: 16, color: '#F2C811' }} />
      </Tooltip>
    );
  }

  // Fall back to isSnowflakeTask for backwards compatibility
  if (isSnowflakeTask) {
    return (
      <Tooltip title="Snowflake Task (auto-synced)" arrow>
        <AcUnitIcon sx={{ fontSize: 16, color: '#29B5E8' }} />
      </Tooltip>
    );
  }

  return null;
};

/**
 * Category Card for Automations
 */
const AutomationCategoryCard = ({
  categoryKey,
  categoryLabel,
  categoryHex,
  CategoryIcon,
  automations,
  isSelected,
  onClick,
  loading,
}) => {
  const statusCounts = useMemo(() => {
    const counts = { success: 0, failed: 0, never: 0 };
    automations.forEach((a) => {
      if (!a.lastRunStatus) {
        counts.never++;
      } else if (a.lastRunStatus === 'success') {
        counts.success++;
      } else if (a.lastRunStatus === 'failed') {
        counts.failed++;
      }
    });
    return counts;
  }, [automations]);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        flex: '1 1 0',
        minWidth: 180,
        minHeight: 100,
        cursor: 'pointer',
        borderRadius: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? categoryHex : 'divider',
        borderTop: `4px solid ${categoryHex}`,
        backgroundColor: isSelected ? alpha(categoryHex, 0.07) : 'white',
        boxShadow: isSelected ? `0 2px 8px ${alpha(categoryHex, 0.18)}` : 'none',
        px: 2,
        py: 1.5,
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: alpha(categoryHex, 0.06),
        },
      }}
    >
      {/* Icon + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CategoryIcon sx={{ color: categoryHex, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
          {categoryLabel}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: categoryHex }} />
        </Box>
      ) : (
        <>
          {/* Automation count */}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {automations.length} automation{automations.length !== 1 ? 's' : ''}
          </Typography>

          {automations.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {statusCounts.failed > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: STATUS_COLORS.failed.color }} />
                  <Typography variant="caption" sx={{ color: STATUS_COLORS.failed.color, fontWeight: 600 }}>
                    {statusCounts.failed}
                  </Typography>
                </Box>
              )}
              {statusCounts.success > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: STATUS_COLORS.success.color }} />
                  <Typography variant="caption" sx={{ color: STATUS_COLORS.success.color, fontWeight: 600 }}>
                    {statusCounts.success}
                  </Typography>
                </Box>
              )}
              {statusCounts.never > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: STATUS_COLORS.never.color }} />
                  <Typography variant="caption" sx={{ color: STATUS_COLORS.never.color, fontWeight: 600 }}>
                    {statusCounts.never}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              No automations
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

const AutomationsDashboard = ({ firebaseUser, triggerAddAutomation }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [viewMode, setViewMode] = useState('list');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [orderBy, setOrderBy] = useState('name');
  const [orderDir, setOrderDir] = useState('asc');
  const [nameFilter, setNameFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');

  // Run Now dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(null);
  const [runDates, setRunDates] = useState({ startDate: null, endDate: null });
  const [running, setRunning] = useState(false);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const headers = { Authorization: `Bearer ${token}` };
      const [autoRes, authRes] = await Promise.all([
        axios.get('/hd-api/data-integrity-2/automations', { headers }),
        axios.get('/hd-api/data-integrity-2/procedure-sources', { headers }),
      ]);

      setAutomations(autoRes.data.automations || []);
      setAuthorized(!!authRes.data.authorized);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching automations:', err);
      setError('Failed to load automations.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (firebaseUser?.getIdToken) {
      fetchAutomations();
    }
  }, [firebaseUser]);

  // Respond to external trigger to open add form
  const prevTriggerRef = useRef(triggerAddAutomation);
  useEffect(() => {
    // Only open form if trigger value actually changed (not on initial mount)
    if (triggerAddAutomation > 0 && triggerAddAutomation !== prevTriggerRef.current) {
      setEditingAutomation(null);
      setFormOpen(true);
    }
    prevTriggerRef.current = triggerAddAutomation;
  }, [triggerAddAutomation]);

  // Group automations by category
  const automationsByCategory = useMemo(() => {
    const grouped = {};
    CATEGORY_ORDER.forEach((cat) => {
      grouped[cat] = [];
    });
    automations.forEach((a) => {
      const cat = a.categoryKey || 'other';
      if (grouped[cat]) {
        grouped[cat].push(a);
      } else {
        grouped.other.push(a);
      }
    });
    return grouped;
  }, [automations]);

  const handleCategoryClick = (categoryKey) => {
    setSelectedCategory((prev) => (prev === categoryKey ? null : categoryKey));
    setNameFilter('');
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrderDir('asc');
    }
  };

  // Filter automations based on selected category, name, and platform
  // When no category selected, show all automations
  const filteredAutomations = useMemo(() => {
    let list = selectedCategory ? automationsByCategory[selectedCategory] : automations;
    if (nameFilter) {
      list = list.filter((a) => a.name.toLowerCase().includes(nameFilter.toLowerCase()));
    }
    if (platformFilter) {
      list = list.filter((a) => {
        if (platformFilter === 'snowflake') {
          return a.platformType === 'snowflake' || a.isSnowflakeTask;
        }
        return a.platformType === platformFilter;
      });
    }
    return list;
  }, [selectedCategory, automationsByCategory, automations, nameFilter, platformFilter]);

  const sortedAutomations = useMemo(() => {
    return [...filteredAutomations].sort((a, b) => {
      let aVal = a[orderBy] ?? '';
      let bVal = b[orderBy] ?? '';

      if (orderBy === 'lastRunAt') {
        aVal = a.lastRunAt ? new Date(a.lastRunAt).getTime() : 0;
        bVal = b.lastRunAt ? new Date(b.lastRunAt).getTime() : 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return orderDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return orderDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAutomations, orderBy, orderDir]);

  const handleAdd = () => {
    setEditingAutomation(null);
    setFormOpen(true);
  };

  const handleEdit = (automation) => {
    setEditingAutomation(automation);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      const token = await firebaseUser.getIdToken(true);
      await axios.delete(`/hd-api/data-integrity-2/automations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmId(null);
      await fetchAutomations();
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation.');
    }
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      if (editingAutomation) {
        await axios.put(
          `/hd-api/data-integrity-2/automations/${editingAutomation.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post('/hd-api/data-integrity-2/automations', formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setSaving(false);
      setFormOpen(false);
      setEditingAutomation(null);
      await fetchAutomations();
    } catch (err) {
      console.error('Error saving automation:', err);
      setError(err.response?.data?.error || 'Failed to save automation.');
      setSaving(false);
    }
  };

  const handleOpenRunDialog = (automation) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setRunningAutomation(automation);
    setRunDates({ startDate: yesterday, endDate: yesterday });
    setRunDialogOpen(true);
  };

  const handleCloseRunDialog = () => {
    setRunDialogOpen(false);
    setRunningAutomation(null);
  };

  const handleRunProcedure = async () => {
    if (!runningAutomation || !runDates.startDate || !runDates.endDate) return;

    try {
      setRunning(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const formatDate = (d) => d.toISOString().split('T')[0];

      await axios.post(
        `/hd-api/data-integrity-2/automations/${runningAutomation.id}/run`,
        {
          startDate: formatDate(runDates.startDate),
          endDate: formatDate(runDates.endDate),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRunning(false);
      setRunDialogOpen(false);
      setRunningAutomation(null);
      await fetchAutomations();
    } catch (err) {
      console.error('Error running procedure:', err);
      setError(err.response?.data?.error || 'Failed to run procedure.');
      setRunning(false);
    }
  };

  const getStatusChip = (automation) => {
    if (!automation.lastRunStatus) {
      return <Chip label="Never Run" size="small" variant="outlined" sx={{ fontSize: 11 }} />;
    }

    const colorMap = {
      success: 'success',
      failed: 'error',
      running: 'warning',
    };

    return (
      <Tooltip title={automation.lastRunMessage || ''} arrow>
        <Chip
          label={automation.lastRunStatus.charAt(0).toUpperCase() + automation.lastRunStatus.slice(1)}
          size="small"
          color={colorMap[automation.lastRunStatus] || 'default'}
          variant="outlined"
          sx={{ fontSize: 11, fontWeight: 600 }}
        />
      </Tooltip>
    );
  };

  const selectedCategoryHex = selectedCategory ? CATEGORY_COLORS[selectedCategory] : '#1976d2'; // default blue for "all"

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
          {error}
        </Typography>
      )}

      {/* Category Cards Row */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {CATEGORY_ORDER.map((categoryKey) => {
          const catAutomations = automationsByCategory[categoryKey] || [];
          return (
            <AutomationCategoryCard
              key={categoryKey}
              categoryKey={categoryKey}
              categoryLabel={CATEGORY_LABELS[categoryKey]}
              categoryHex={CATEGORY_COLORS[categoryKey]}
              CategoryIcon={CATEGORY_ICONS[categoryKey]}
              automations={catAutomations}
              isSelected={selectedCategory === categoryKey}
              onClick={() => handleCategoryClick(categoryKey)}
              loading={loading}
            />
          );
        })}
      </Box>

      {!loading && (
        <>
          {/* Detail Panel */}
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderTop: `3px solid ${selectedCategoryHex}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Detail Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: alpha(selectedCategoryHex, 0.04),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {selectedCategory ? (
                React.createElement(CATEGORY_ICONS[selectedCategory], {
                  sx: { color: selectedCategoryHex, fontSize: 20 },
                })
              ) : null}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                {selectedCategory ? `${CATEGORY_LABELS[selectedCategory]} Automations` : 'All Automations'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                ({filteredAutomations.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => val && setViewMode(val)}
                size="small"
                sx={{ mr: 1 }}
              >
                <ToggleButton value="list" sx={{ py: 0.25, px: 1 }}>
                  <Tooltip title="List view" arrow><ViewListIcon sx={{ fontSize: 18 }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="calendar" sx={{ py: 0.25, px: 1 }}>
                  <Tooltip title="Calendar view" arrow><CalendarMonthIcon sx={{ fontSize: 18 }} /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <NativeSelect
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                input={<CompactInput />}
                sx={{ minWidth: 130 }}
              >
                {PLATFORM_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect>
              <CompactInput
                placeholder="Filter by name..."
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{ width: 200, ml: 1 }}
              />
            </Box>

                {viewMode === 'calendar' ? (
                  <AutomationsCalendar
                    automations={automations}
                    firebaseUser={firebaseUser}
                    selectedCategory={selectedCategory}
                    platformFilter={platformFilter}
                    nameFilter={nameFilter}
                  />
                ) : (
                  <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {[
                            { id: 'name', label: 'Name' },
                            { id: 'scheduleCron', label: 'Schedule' },
                            { id: 'lastRunAt', label: 'Last Run' },
                            { id: 'lastRunStatus', label: 'Status' },
                            { id: 'nextRun', label: 'Next Run' },
                          ].map((col) => (
                            <TableCell key={col.id} sx={{ fontWeight: 700, fontFamily: 'Lato' }}>
                              <TableSortLabel
                                active={orderBy === col.id}
                                direction={orderBy === col.id ? orderDir : 'asc'}
                                onClick={() => handleSort(col.id)}
                              >
                                {col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                          {authorized && (
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Actions</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedAutomations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={authorized ? 6 : 5} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No automations in this category
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedAutomations.map((automation) => (
                            <TableRow key={automation.id} hover>
                              <TableCell>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {automation.name}
                                    </Typography>
                                    {getPlatformIcon(automation)}
                                  </Box>
                                  {automation.description && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {automation.description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={automation.scheduleCron} arrow>
                                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                                    {getCronReadable(automation.scheduleCron)}
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {automation.scheduleTimezone}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatTimestamp(automation.lastRunAt)}</TableCell>
                              <TableCell>{getStatusChip(automation)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: 13 }}>
                                  {getNextRun(automation.scheduleCron, automation.scheduleTimezone)}
                                </Typography>
                              </TableCell>
                              {authorized && (
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {deleteConfirmId === automation.id ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="contained"
                                        onClick={() => handleDelete(automation.id)}
                                        sx={{ textTransform: 'none', fontSize: 11, py: 0, minWidth: 0, px: 1 }}
                                      >
                                        Confirm
                                      </Button>
                                      <Button
                                        size="small"
                                        onClick={() => setDeleteConfirmId(null)}
                                        sx={{ textTransform: 'none', fontSize: 11, py: 0, minWidth: 0, px: 1 }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  ) : (
                                    <>
                                      {automation.procedureName && (
                                        <Tooltip title="Run Now">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOpenRunDialog(automation)}
                                            sx={{ color: 'success.main' }}
                                          >
                                            <PlayArrowIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => handleEdit(automation)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => setDeleteConfirmId(automation.id)}>
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
        </>
      )}

      <AutomationFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingAutomation(null);
        }}
        onSave={handleSave}
        automation={editingAutomation}
        saving={saving}
      />

      {/* Run Now Dialog */}
      <Dialog open={runDialogOpen} onClose={handleCloseRunDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontFamily: 'Lato',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Run Procedure
          <IconButton onClick={handleCloseRunDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {runningAutomation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {runningAutomation.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
                {runningAutomation.procedureName}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Start Date
                  </Typography>
                  <DatePicker
                    selected={runDates.startDate}
                    onChange={(date) => setRunDates((prev) => ({ ...prev, startDate: date }))}
                    dateFormat="yyyy-MM-dd"
                    className="di2-datepicker"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    End Date
                  </Typography>
                  <DatePicker
                    selected={runDates.endDate}
                    onChange={(date) => setRunDates((prev) => ({ ...prev, endDate: date }))}
                    dateFormat="yyyy-MM-dd"
                    className="di2-datepicker"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button
                  onClick={handleCloseRunDialog}
                  sx={{ textTransform: 'none', fontFamily: 'Lato' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleRunProcedure}
                  disabled={running || !runDates.startDate || !runDates.endDate}
                  startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  sx={{ textTransform: 'none', fontFamily: 'Lato' }}
                >
                  {running ? 'Running...' : 'Run'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AutomationsDashboard;
